<template>
  <div class="bl-client-targets-panel">
    <div class="bl-targets-header">
      <div class="bl-targets-name">
        <BlTextTitle4 text="Targets" />
        <BlBubbleCount :count="totalTargets || 0" />
      </div>
      <div class="bl-targets-description">
        <BlTextBody2
          text="Create targets tailored to your clients and use them in their sessions."
        />
      </div>
      <div class="relative flex items-center gap-4">
        <BlInputButton
          label="Create new target"
          class="flex-grow"
          icon="plus"
          :disabled="hasTargetJob"
          @clicked="$emit('toggle-new-target-opened', true)"
        />
        <div
          class="flex items-center justify-center flex-shrink-0 w-10 h-full rounded cursor-pointer bg-light-purple"
          :class="[
            hasTargetJob
              ? 'pointer-events-none opacity-25'
              : 'hover:bg-dark-purple-200'
          ]"
          :disabled="hasTargetJob"
          @click="menuOpened = !menuOpened"
        >
          <BlIcon
            icon="chevron-down"
            color="secondary"
            class="w-6 h-6 transition-all duration-300"
            :style="{ rotate: menuOpened ? '180deg' : '' }"
          />
        </div>
        <div
          v-if="menuOpened"
          class="absolute right-0 z-50 p-1 mt-2 space-y-1 border rounded top-full bg-pure-white border-grey-200"
          @click="menuOpened = false"
        >
          <div
            class="px-4 py-2 text-left rounded cursor-pointer hover:bg-hover-state min-w-32"
            @click="addTargetFromCurriculaOpened = true"
          >
            <BlTextBody2
              class="text-slate-1000"
              text="Add targets from curricula"
            />
          </div>
          <div
            class="px-4 py-2 text-left rounded cursor-pointer hover:bg-hover-state min-w-32"
            @click="addTargetFromDatabankOpened = true"
          >
            <BlTextBody2
              class="text-slate-1000"
              text="Add targets from databank"
            />
          </div>
        </div>
      </div>
      <div class="bl-targets-search">
        <BlInputSearch
          :input="searchInput"
          @changed="inputHandler"
          class="w-full"
        />
      </div>
      <div class="grid w-full grid-cols-2 gap-4">
        <BlInputFilter
          class="bl-targets-header-filter"
          :input="filterInput"
          @changed="inputHandler"
        />
        <BlInputSelect
          class="bl-targets-header-filter"
          :input="sortInput"
          @changed="inputHandler"
        />
      </div>
    </div>

    <div class="bl-targets-body">
      <div v-if="loading" class="absolute z-10 w-full h-full -mx-6 -my-6">
        <div class="absolute w-full h-full opacity-75 bg-pure-white"></div>
        <div class="absolute z-10 grid w-full h-full place-items-center">
          <div class="blum-loader"></div>
        </div>
      </div>
      <div
        v-else-if="!targets.length"
        class="flex items-center justify-center h-full text-sm text-grey-400"
      >
        <BlTextBody1 text="Target(s) not found." />
      </div>
      <TargetCard
        v-else
        v-for="item in targets"
        :key="item.id"
        class="bl-client-target"
        :class="{ 'my-2' : item.target_tasks && item.target_tasks.length > 0 }"
        useAction
        @clicked="onSelectTarget(item.id)"
        @clickedTask="onSelectTargetTasks"
        @delete="deleteOpened = item.id"
        :target="item"
        :activeTask="activeTask"
        :active="target ? target.id === item.id : false"
      />
    </div>

    <NewTargetOverlay
      v-if="newTargetOpened"
      description="Create a new target tailored for your clients."
      :clientId="id"
      kind="client"
      @close-modal="$emit('toggle-new-target-opened', false)"
    />

    <AddTargetFromCurriculaModal
      v-if="addTargetFromCurriculaOpened"
      @close-modal="addTargetFromCurriculaOpened = false"
    />
    <AddTargetFromDatabankModal
      v-if="addTargetFromDatabankOpened"
      @close-modal="addTargetFromDatabankOpened = false"
    />

    <BlBackdrop
      v-if="deleteOpened > -1"
      title="Delete target"
      label="Delete the target"
      description="You are about to delete the target. Are you sure you want to proceed? This action is irreversible."
      type="error"
      empty
      @backdrop-close="deleteOpened = -1"
      @backdrop-action="onRemoveTarget"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import TargetCard from "./Card.vue";
import NewTargetOverlay from "./overlay/NewTargetOverlay.vue";
import AddTargetFromCurriculaModal from "./modal/AddTargetFromCurriculaModal.vue";
import AddTargetFromDatabankModal from "./modal/AddTargetFromDatabankModal.vue";

import BlBubbleCount from "../../BlBubbleCount.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputSelect from "../../BlInputSelect.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "TargetPanel",
  components: {
    TargetCard,
    NewTargetOverlay,
    AddTargetFromCurriculaModal,
    AddTargetFromDatabankModal,
    BlTextTitle4,
    BlBubbleCount,
    BlTextBody2,
    BlTextSubtitle2,
    BlInputButton,
    BlInputSearch,
    BlInputFilter,
    BlInputSelect,
    BlBackdrop,
    BlTextBody1,
    BlIcon
  },
  props: ["id", "newTargetOpened", "taskOfTarget", "activeTask"],
  data() {
    const defaultFilter = [[], [], []];
    return {
      loading: false,
      menuOpened: false,
      addTargetFromCurriculaOpened: false,
      addTargetFromDatabankOpened: false,
      searchTimeout: null,
      searchInput: { placeholder: "Search by keyword", model: "" },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          {
            name: "Curriculum",
            kind: "multi",
            options: [],
            url: `/api/v1/curriculums?sort=name_asc&query=`,
            fetchPage: 1,
            dataKey: "curriculums",
            countKey: "total_count",
            optionKeys: { value: "id", label: "name", prefixColor: "color" }
          },
          {
            name: "Status",
            kind: "multi-tag",
            options: [
              { value: "pending", chip: true },
              { value: "in_acquisition", chip: true },
              { value: "paused", chip: true },
              { value: "mastered", chip: true },
              { value: "discontinued", chip: true }
            ]
          },
          {
            name: "Method",
            kind: "multi",
            options: []
          }
        ]
      },
      sortInput: {
        prefix: "Sort by",
        model: "",
        options: [
          { name: "Most recent", value: "*" },
          { name: "Alphabetical", value: "alphabetical" }
        ]
      },
      deleteOpened: -1,
      task: null
    };
  },
  computed: {
    ...mapGetters([
      "curriculums",
      "target",
      "targets",
      "totalTargets",
      "clientTargetJob",
      "currentCenter"
    ]),
    query() {
      let params = `?client_id=${this.id}`;
      const query = this.searchInput.model;
      const sort =
        this.sortInput.model && this.sortInput.model.value !== "*"
          ? this.sortInput.model.value
          : "";
      const status = this.filterInput.model[1]
        .map(a => a.value)
        .join(",")
        .replaceAll("in_acquisition", "in_progress");
      const curriculumIds = this.filterInput.model[0]
        ? this.filterInput.model[0].map(a => a.value).join(",")
        : "";
      const type = this.filterInput.model[2]
        ? this.filterInput.model[2].map(a => a.value).join(",")
        : "";
      if (query) params += `&query=${query}`;
      if (sort) params += `&sort=${sort}`;
      if (curriculumIds) params += `&curriculum_ids=${curriculumIds}`;
      if (type) params += `&type=${type}`;
      if (status) params += `&status=${status}`;
      return params;
    },
    hasTargetJob() {
      if (!this.clientTargetJob) return false;
      const status = this.clientTargetJob.status;
      return status !== "completed" && status !== "canceled";
    }
  },
  watch: {
    query() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(this.refresh, 500);
    },
    currentCenter() {
    // Update method options whenever currentCenter changes
      this.updateMethodOptions();
    }
  },
  mounted() {
    this.loading = true;
    this.setTargets([]);
    this.setEntries([]);
    this.setTarget(null);
    this.updateMethodOptions();
    this.refresh();
  },
  methods: {
    ...mapActions(["getTarget", "getTargets", "deleteTarget"]),
    ...mapMutations(["setTarget", "setTargets", "setEntries"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async refresh() {
      this.loading = true;
      this.getTargets({ params: this.query }).then(() => {
        this.loading = false;
        this.setEntries([]);
        this.setTarget(null);
      });
    },
    onSelectTarget(id) {
      if (this.activeTask) {
        this.$emit("toggle-active-task", null);
      }
      this.$emit("toggle-target-loading", true);
      this.getTarget({ id }).then(() => {
        this.$emit("toggle-target-loading", false);
      });
    },
    onSelectTargetTasks(data, targetId) {
      if (!data || !targetId) return null;
      this.$emit("toggle-target-loading", true);
      this.getTarget({ id: targetId }).then(({data: target}) => {
        const findTaskFromTarget = target.target_tasks.find(i => i.id === data.id);
        if (findTaskFromTarget) this.$emit("toggle-active-task", findTaskFromTarget)
        this.task = data;
        this.$emit("toggle-target-loading", false);
      });
    },
    onRemoveTarget() {
      const data = {
        kind: "client",
        id: this.deleteOpened,
        name: null
      };
      const temp = this.targets.find(
        i => Number(i.id) === Number(this.deleteOpened)
      );
      if (temp) data.name = temp.name;
      this.deleteTarget(data).then(() => {
        this.deleteOpened = -1;
      });
    },
    updateMethodOptions() {
      const methodOptions = [
        { name: "Percentage", value: "Target::Percentage" },
        { name: "Duration", value: "Target::Duration" },
        { name: "Partial interval recording", value: "Target::Pir" },
        { name: "Frequency", value: "Target::Frequency" },
        { name: "Prompting", value: "Target::Prompting" }
      ];

      if (this.currentCenter && this.currentCenter.enable_sbt) {
        methodOptions.push({ name: "Skill-Based Treatment (SBT)", value: "Target::Sbt" });
      }

      // Update the options in the filterInput
      this.filterInput.tabs[2].options = methodOptions;
    }
  }
};
</script>

<style scoped lang="scss">
.bl-client-targets-panel {
  position: relative;
  width: 30rem;
  height: 100%;
  background: var(--background-depth-one-color);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex: 1;
  flex-direction: column;
  .bl-targets-header {
    position: relative;
    width: 100%;
    padding: 18px;
    box-sizing: border-box;
    border-bottom: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    .bl-targets-name {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .bl-targets-description {
      position: relative;
      width: 100%;
      margin-top: -0.5rem;
    }
    .bl-targets-search {
      position: relative;
      width: 100%;
      display: flex;
    }
  }
  .bl-targets-body {
    position: relative;
    width: 100%;
    padding: 12px;
    box-sizing: border-box;
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow-y: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/Panel.vue